import {
  DocEm,
  DocH2,
  DocH3,
  DocHr,
  DocLink,
  DocP,
  DocTable,
  DocUl,
} from "@/components/ec/documentSection";

export function TermsContent() {
  return (
    <>
      <DocP>
        These Terms of Service (“Terms”) govern your access to and use of the Autranslate Shopify
        application (the “App”) provided by Autranslate (“we”, “us”, “our”). By installing or using
        the App, you agree to these Terms.
      </DocP>
      <DocP>If you do not agree, do not install or use the App.</DocP>

      <DocHr />

      <DocH2>1. The service</DocH2>
      <DocP>Autranslate helps Shopify merchants:</DocP>
      <DocUl>
        <li>
          Generate AI-assisted translations for product content (title, description, SEO fields, and
          related translatable resources)
        </li>
        <li>Review and edit translations before publishing</li>
        <li>Publish approved translations to Shopify using Shopify’s translation APIs</li>
        <li>Manage translation workflow status by language and product</li>
      </DocUl>
      <DocP>
        The App integrates with <strong>Shopify locales</strong> enabled on your store. Supported
        languages depend on your Shopify plan and store configuration.
      </DocP>

      <DocHr />

      <DocH2>2. Eligibility</DocH2>
      <DocP>You must:</DocP>
      <DocUl>
        <li>Be a merchant with a valid Shopify store</li>
        <li>Have authority to bind your business to these Terms</li>
        <li>Comply with Shopify’s terms, policies, and applicable laws</li>
      </DocUl>

      <DocHr />

      <DocH2>3. Subscription and fees</DocH2>

      <DocH3>3.1 Paid plan</DocH3>
      <DocP>The App is offered as a paid subscription through <strong>Shopify Billing</strong>:</DocP>
      <DocTable>
        <thead>
          <tr>
            <th>Component</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Base subscription</strong>
            </td>
            <td>
              <strong>USD $5.00 per month</strong> (billed every 30 days via Shopify)
            </td>
          </tr>
          <tr>
            <td>
              <strong>Included usage</strong>
            </td>
            <td>
              <strong>10,000 translation credits per billing period</strong> (monthly allowance)
            </td>
          </tr>
          <tr>
            <td>
              <strong>Overage</strong>
            </td>
            <td>Usage beyond the included credits is billed as <strong>usage charges</strong> through Shopify</td>
          </tr>
          <tr>
            <td>
              <strong>Overage rate</strong>
            </td>
            <td>
              <strong>USD $1.00 per 1,000 credits</strong> over the monthly allowance (unless otherwise stated in the
              App or billing approval screen)
            </td>
          </tr>
          <tr>
            <td>
              <strong>Usage cap</strong>
            </td>
            <td>
              Usage charges may be subject to a capped amount per billing period (default{" "}
              <strong>USD $20.00</strong> per 30 days as shown at subscription approval; configurable in our billing
              configuration)
            </td>
          </tr>
        </tbody>
      </DocTable>
      <DocP>
        Exact amounts, caps, and terms displayed on Shopify’s <strong>subscription approval screen</strong> at install
        or plan change control and form part of your agreement with us for billing purposes.
      </DocP>

      <DocH3>3.2 Billing through Shopify</DocH3>
      <DocP>
        All charges are processed by Shopify. You authorize Shopify to charge your store’s payment method on file with
        Shopify. Refunds, disputes, and payment failures are subject to Shopify’s policies and billing flows.
      </DocP>

      <DocH3>3.3 Trials, plan changes, and cancellation</DocH3>
      <DocUl>
        <li>Any free trial, if offered, will be shown on the Shopify approval screen.</li>
        <li>You may cancel or change plans according to Shopify’s app billing interfaces.</li>
        <li>
          Uninstalling the App does not automatically erase translation content already published to your store; you
          are responsible for managing storefront content in Shopify admin.
        </li>
      </DocUl>

      <DocH3>3.4 Taxes</DocH3>
      <DocP>
        Fees are exclusive of applicable taxes unless stated otherwise. You are responsible for taxes imposed on your
        purchases where applicable.
      </DocP>

      <DocHr />

      <DocH2>4. Credits and usage</DocH2>
      <DocUl>
        <li>“Credits” are units consumed when you run AI translation jobs, as calculated by the App.</li>
        <li>Monthly included credits reset according to your billing period configuration in the App.</li>
        <li>
          If credits are insufficient and usage billing is enabled and approved, overage charges may apply as described
          in Section 3.
        </li>
        <li>
          We may apply reasonable technical limits to protect service stability (rate limits, concurrency, or maximum
          batch sizes).
        </li>
      </DocUl>

      <DocHr />

      <DocH2>5. AI-generated content disclaimer</DocH2>
      <DocP>Translations are generated using artificial intelligence. You are solely responsible for:</DocP>
      <DocUl>
        <li>Reviewing all content before publication</li>
        <li>Ensuring accuracy, legal compliance, and brand suitability</li>
        <li>Compliance with consumer protection, advertising, and localization laws in your markets</li>
      </DocUl>
      <DocP>
        We do not guarantee that AI output is error-free, complete, or suitable for any particular purpose.{" "}
        <strong>Always review before publishing.</strong>
      </DocP>

      <DocHr />

      <DocH2>6. Acceptable use</DocH2>
      <DocP>You agree not to:</DocP>
      <DocUl>
        <li>Use the App for unlawful, harmful, or infringing content</li>
        <li>Attempt to bypass billing, credit limits, or security controls</li>
        <li>Reverse engineer, scrape, or abuse the service in ways that harm other merchants or infrastructure</li>
        <li>Use the App in violation of Shopify’s Acceptable Use Policy or Partner Program Agreement</li>
      </DocUl>
      <DocP>We may suspend or terminate access for material violations.</DocP>

      <DocHr />

      <DocH2>7. Intellectual property</DocH2>
      <DocUl>
        <li>You retain ownership of your store content.</li>
        <li>You grant us a limited license to process your content solely to provide the App.</li>
        <li>We retain ownership of the App, software, branding, and documentation.</li>
      </DocUl>

      <DocHr />

      <DocH2>8. Third-party services</DocH2>
      <DocP>
        The App relies on Shopify and third-party providers (including AI and hosting services). Their availability,
        terms, and outages may affect the App. We are not responsible for third-party services outside our reasonable
        control.
      </DocP>

      <DocHr />

      <DocH2>9. Disclaimer of warranties</DocH2>
      <DocP>
        THE APP IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
        WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
        NON-INFRINGEMENT.
      </DocP>

      <DocHr />

      <DocH2>10. Limitation of liability</DocH2>
      <DocP>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</DocP>
      <DocUl>
        <li>
          WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOST PROFITS,
          REVENUE, DATA, OR GOODWILL.
        </li>
        <li>
          OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THE APP IS LIMITED TO THE FEES YOU PAID TO US FOR THE APP IN
          THE <strong>THREE (3) MONTHS</strong> BEFORE THE EVENT GIVING RISE TO THE CLAIM.
        </li>
      </DocUl>
      <DocP>
        Some jurisdictions do not allow certain limitations; in those cases, our liability is limited to the fullest
        extent permitted by law.
      </DocP>

      <DocHr />

      <DocH2>11. Indemnification</DocH2>
      <DocP>
        You agree to indemnify and hold us harmless from claims arising from your store content, your use of the App, or
        your violation of these Terms or applicable law.
      </DocP>

      <DocHr />

      <DocH2>12. Data protection</DocH2>
      <DocP>
        Our collection and use of data is described in the{" "}
        <DocLink href="/service/ec/privacypolicy">Privacy Policy</DocLink>. By using the App, you acknowledge that
        policy.
      </DocP>

      <DocHr />

      <DocH2>13. Termination</DocH2>
      <DocUl>
        <li>You may stop using the App by uninstalling it from your Shopify store.</li>
        <li>
          We may terminate or suspend access if you breach these Terms or if required for security or legal reasons.
        </li>
        <li>
          Sections that by nature should survive (fees owed, disclaimers, limitations, indemnity) survive termination.
        </li>
      </DocUl>

      <DocHr />

      <DocH2>14. Changes to these Terms</DocH2>
      <DocP>
        We may update these Terms by posting a revised version with an updated date. Material changes may also be
        communicated through the App or email where appropriate. Continued use after the effective date constitutes
        acceptance.
      </DocP>

      <DocHr />

      <DocH2>15. Governing law</DocH2>
      <DocP>
        These Terms are governed by the laws of <strong>Japan</strong>, without regard to conflict-of-law principles,
        unless mandatory local consumer protection laws require otherwise.
      </DocP>

      <DocHr />

      <DocH2>16. Contact</DocH2>
      <DocP>
        <strong>Support / legal inquiries:</strong>{" "}
        <DocLink href="mailto:yosui262@gmail.com">yosui262@gmail.com</DocLink>
        <br />
        <strong>Emergency developer contact (App Review):</strong>{" "}
        <DocLink href="mailto:yosui262@gmail.com">yosui262@gmail.com</DocLink>
      </DocP>

      <DocHr />

      <DocEm>
        This document is provided for App Store listing and merchant transparency. It does not constitute legal advice.
      </DocEm>
    </>
  );
}
